import { Skeleton } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";

import { getListJobsQueryKey, useGetCatalog } from "../../../api/generated/endpoints/generation/generation";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { ActorsComposer } from "../../../platform/actors/composer/ActorsComposer";
import { actorModels } from "../../../platform/actors/composer/GenerateWithText";
import { arrivingTalkingHeadActor, tookTalkingHeadActor } from "../../../platform/actors/talking-head-handoff";
import { advancedModels } from "../../../platform/studio/image/advanced";
import { TalkingHeadForm } from "../../../platform/studio/tools/talking-head/TalkingHeadForm";
import create from "../create/create.module.css";
import { ImageForm } from "../create/ImageForm";
import { VideoForm } from "../create/VideoForm";
import { RecentResults } from "../create/RecentResults";
import home from "../pro-home.module.css";
import { ACTOR_TYPES, AD_STATIC_TYPES, TALKING_TYPES } from "./home-pictures";
import { SOUND_PAGES } from "./sounds";
import { toolOf } from "./video-tools";
import classes from "./simple-home.module.css";

const BRAND = "/signed-in/teams/$teamId/brands/$brandId";
const NO_EVENT = () => undefined;
const EXACT = { exact: true };

interface FlowProps {
  /** The page it was opened from: Home, whose cards open most, or a section. */
  back?: "Images" | "Videos" | undefined;
  title: string;
  line: string;
  composer: ReactNode;
  results: ReactNode;
}

/**
 * A Home card's flow in Simple's frame (spec ui-perf/simple-flows, "The flows"), or a section
 * tile's (spec ui-perf/simple-images): the way back, the card's name and line, then Pro's composer
 * card beside its recent results, as Pro's Create pages lay them out.
 */
function Flow({ back, title, line, composer, results }: FlowProps) {
  const { teamId, brandId } = useParams({ from: BRAND });
  return (
    <div className={create.page}>
      {/* Exact: under Home's path, a link to it would otherwise say it is the page on screen. */}
      <Link
        to={
          back === "Images"
            ? "/teams/$teamId/brands/$brandId/simple/images"
            : back === "Videos"
              ? "/teams/$teamId/brands/$brandId/simple/videos"
              : "/teams/$teamId/brands/$brandId/simple"
        }
        params={{ teamId, brandId }}
        activeOptions={EXACT}
        className={`${home.subtle} ${classes.back}`}
      >
        <ArrowLeft size={14} aria-hidden /> {back ?? "Home"}
      </Link>
      <div className={classes.flowHead}>
        <h1 className={create.title}>{title}</h1>
        <p className={classes.flowLine}>{line}</p>
      </div>
      <div className={create.create}>
        <div className={create.composer}>{composer}</div>
        <section aria-label="Results">{results}</section>
      </div>
    </div>
  );
}

/** UGC video ad: the Studio's Talking Head form, as Pro's Talking head page draws it. */
export function SimpleUgcPage() {
  const { teamId, brandId } = useParams({ from: BRAND });
  const catalog = useGetCatalog();
  // An actor's "Make a video" (Brand's Actors) brings it here once, as Pro's Talking head page
  // takes one: the navigation's actor, marked taken (spec ui-perf/simple-brand-tabs).
  const location = useLocation();
  const [arrival] = useState(() => arrivingTalkingHeadActor(location));
  useEffect(() => {
    if (arrival !== null) tookTalkingHeadActor(location);
  }, [arrival, location]);
  const offered = catalog.data?.video_models.some((each) => each.job_types.includes("video.talking_head"));
  const composer = catalog.isError ? (
    <ErrorAlert error={catalog.error} />
  ) : catalog.data === undefined ? (
    <Skeleton className={create.composerSkeleton} />
  ) : offered === true ? (
    // A brand's own draft, as Pro's page keys it.
    <TalkingHeadForm
      key={brandId}
      catalog={catalog.data}
      above={undefined}
      overlay={undefined}
      onStarted={NO_EVENT}
      actor={arrival}
      inline
      styles
    />
  ) : (
    <p className={create.dim}>No model makes talking heads yet.</p>
  );
  return (
    <Flow
      title="UGC video ad"
      line="A person talks about your product to the camera"
      composer={composer}
      results={<RecentResults teamId={teamId} brandId={brandId} types={TALKING_TYPES} noun="talking head" />}
    />
  );
}

/** Image ad: Pro's image form on Ad static; its Image is Simple's image flow, the guided page's. */
export function SimpleImageAdPage() {
  const { teamId, brandId } = useParams({ from: BRAND });
  const navigate = useNavigate();
  const catalog = useGetCatalog();
  const composer = catalog.isError ? (
    <ErrorAlert error={catalog.error} />
  ) : catalog.data === undefined ? (
    <Skeleton className={create.composerSkeleton} />
  ) : advancedModels(catalog.data).length > 0 ? (
    <ImageForm
      key={brandId}
      catalog={catalog.data}
      mode="ad-static"
      onMode={(next) => {
        if (next === "image") {
          void navigate({
            to: "/teams/$teamId/brands/$brandId/simple/make",
            params: { teamId, brandId },
            search: { kind: "image" },
          });
        }
      }}
    />
  ) : (
    <p className={create.dim}>No model makes image ads yet.</p>
  );
  return (
    <Flow
      title="Image ad"
      line="Your product with a headline and a button"
      composer={composer}
      results={<RecentResults teamId={teamId} brandId={brandId} types={AD_STATIC_TYPES} noun="image ad" />}
    />
  );
}

/** Images' Make an actor: the AI Actors page's composer, as Pro's Actors page's Make tab draws it. */
export function SimpleActorPage() {
  const { teamId, brandId } = useParams({ from: BRAND });
  const queryClient = useQueryClient();
  const catalog = useGetCatalog();
  const composer = catalog.isError ? (
    <ErrorAlert error={catalog.error} />
  ) : catalog.data === undefined ? (
    <Skeleton className={create.composerSkeleton} />
  ) : actorModels(catalog.data.image_models).length > 0 ? (
    // A brand's own draft, as Pro's page keys it. The start reads the lists again, as Pro's Make
    // tab does: an empty list has no running job to poll for while the stream is down.
    <ActorsComposer
      key={brandId}
      teamId={teamId}
      brandId={brandId}
      catalog={catalog.data}
      onStarted={() => void queryClient.invalidateQueries({ queryKey: getListJobsQueryKey(teamId) })}
      inline
    />
  ) : (
    <p className={create.dim}>No model makes actors yet.</p>
  );
  return (
    <Flow
      back="Images"
      title="Make an actor"
      line="A new person for your ads. You get 4 to choose from."
      composer={composer}
      results={<RecentResults teamId={teamId} brandId={brandId} types={ACTOR_TYPES} noun="actor" />}
    />
  );
}

/**
 * A video tool's page (spec ui-perf/simple-video-tools): Videos' tile's name and line, the Studio's
 * own form for the tool in the card, as Pro's Video page draws it under its tab, or Pro's video
 * form opened on the tool's action for one that runs on the composer (spec
 * ui-perf/simple-video-compose-tools), beside the tool's recent results. A start reads the lists
 * again, as Make an actor's does.
 */
export function SimpleToolPage() {
  const { teamId, brandId, tool } = useParams({ from: `${BRAND}/studio/simple/videos/$tool` });
  const queryClient = useQueryClient();
  const catalog = useGetCatalog();
  const { name, line, page } = toolOf(tool);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: getListJobsQueryKey(teamId) });
  const offered = catalog.data?.video_models.some((each) => each.job_types.includes(page.type));
  const composer = catalog.isError ? (
    <ErrorAlert error={catalog.error} />
  ) : catalog.data === undefined ? (
    <Skeleton className={create.composerSkeleton} />
  ) : offered === true ? (
    // A brand's own draft, as Pro's page keys it.
    "Form" in page ? (
      <page.Form
        key={`${brandId} ${tool}`}
        catalog={catalog.data}
        above={undefined}
        overlay={null}
        onStarted={refresh}
        inline
      />
    ) : (
      <VideoForm key={`${brandId} ${tool}`} catalog={catalog.data} initialAction={page.action} onStarted={refresh} />
    )
  ) : (
    <p className={create.dim}>No model does this yet.</p>
  );
  return (
    <Flow
      back="Videos"
      title={name}
      line={line}
      composer={composer}
      results={
        "Form" in page ? (
          <RecentResults teamId={teamId} brandId={brandId} types={[page.type]} noun={page.noun} />
        ) : (
          // Pro's form keeps every tab, so a video started on another shows here too (PR 1768's
          // review): the Studio's video read, Pro's Video page's.
          <RecentResults teamId={teamId} brandId={brandId} medium="video" noun="video" />
        )
      }
    />
  );
}

/**
 * A sound's page (spec ui-perf/simple-sounds), from Create's picker: the tile's name and line, the
 * Sounds page's own form for the kind in the card, beside the kind's recent results. A start reads
 * the lists again, as the Sounds page's does.
 */
export function SimpleSoundPage() {
  const { teamId, brandId, sound } = useParams({ from: `${BRAND}/studio/simple/audio/$sound` });
  const queryClient = useQueryClient();
  const catalog = useGetCatalog();
  const { name, line, type, Form, noun } = SOUND_PAGES[sound];
  const models = catalog.data?.audio_models.filter((each) => each.job_types.includes(type)) ?? [];
  const composer = catalog.isError ? (
    <ErrorAlert error={catalog.error} />
  ) : catalog.data === undefined ? (
    <Skeleton className={create.composerSkeleton} />
  ) : models.length > 0 ? (
    // A brand's own draft, as Pro's page keys it.
    <Form
      key={`${brandId} ${sound}`}
      models={models}
      voiceDesign={catalog.data.audio_models.find((each) => each.job_types.includes("voice.design"))}
      above={undefined}
      onStarted={() => void queryClient.invalidateQueries({ queryKey: getListJobsQueryKey(teamId) })}
      inline
    />
  ) : (
    <p className={create.dim}>No model makes this yet.</p>
  );
  return (
    <Flow
      title={name}
      line={line}
      composer={composer}
      results={<RecentResults teamId={teamId} brandId={brandId} types={[type]} noun={noun} />}
    />
  );
}
